/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { scrollToBreadCrumb, isClient, getAPIConfig, getViewportInfo } from '@tcp/core/src/utils';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import withLazyLoad from '@tcp/core/src/components/common/hoc/withLazyLoad';
import { Heading } from '@tcp/core/styles/themes/TCP/typotheme';
import CategoryPills from '@tcp/core/src/components/features/browse/ProductListing/molecules/CategoryPills';
import ToastModal from '@tcp/core/src/components/features/account/common/molecule/ToastModal/index';
import withStyles from '../../../../common/hoc/withStyles';
import styles, {
  HorizontalBar,
  StyledWrapper,
  StyledSortSkelton,
  StyledPaginatedSkeleton,
  L2TitleSkeleton,
  L2TitleWrapper,
} from '../styles/PaginatedPage.style';
import EmptyCategoryPageView from '../molecules/EmptyCategoryPage/views/EmptyCategoryPage.view';
import {
  calculateSkeletonCount,
  setUpdatedUrl,
  getPlpUrlQueryValuesSeo,
} from '../container/CategoryListingPage.utils';
import PaginatedView from './components/PaginatedView';

const SortByComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "category-filters" */ '../molecules/SortWrapper/views/SortWrapper.view'
    ),
  {
    ssr: false,
    loading: () => {
      return (
        <StyledWrapper>
          <StyledSortSkelton />
        </StyledWrapper>
      );
    },
  }
);

const PaginatorComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "paginator" */ '@tcp/core/src/components/common/molecules/Paginator'
    ),
  {
    ssr: false,
  }
);

const PlpPaginatorComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "new-plp-paginator" */ '@tcp/core/src/components/common/molecules/PlpPaginator'
    ),
  {
    ssr: false,
  }
);

const plpShowingResultpaginator = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "new-plp-paginator" */ '@tcp/core/src/components/common/molecules/plpShowingResultPaginator'
    ),
  {
    ssr: false,
  }
);

const oldPaginator = withLazyLoad(PaginatorComponent);
const newPaginator = withLazyLoad(PlpPaginatorComponent);
const newPlpShowingResultPaginator = withLazyLoad(plpShowingResultpaginator);

export const insertTitleGrid = (paginatedPLP, tileList = []) => {
  const updatedPPLP = paginatedPLP;
  tileList.forEach((title) => {
    const { titleIndex, id, name } = title;
    updatedPPLP[titleIndex] = (
      <Heading key={id} className="item-title" fontFamily="secondaryFontFamily">
        {name}
        <HorizontalBar />
      </Heading>
    );
  });
  return updatedPPLP;
};

export const PaginatedPlpSkeleton = (
  isPaginatedPlpLoading,
  isPaginatorClicked,
  recordsPerPage,
  shouldRenderSkeletonComponent,
  isShowL2Skeleton,
  totalProductGrids
) => {
  return isPaginatedPlpLoading ||
    isPaginatorClicked ||
    isShowL2Skeleton ||
    !totalProductGrids ||
    shouldRenderSkeletonComponent
    ? [...Array(recordsPerPage)].map((_, key) => <StyledPaginatedSkeleton key={key} />)
    : null;
};

const L2CategorySkeleton = (
  isPaginatedPlpLoading,
  isL2Category,
  isInitialCall,
  isShowL2Skeleton
) => {
  return isPaginatedPlpLoading && isL2Category && isInitialCall && isShowL2Skeleton ? (
    <L2TitleWrapper>
      <L2TitleSkeleton />
      <HorizontalBar />
    </L2TitleWrapper>
  ) : null;
};

// eslint-disable-next-line complexity
const PaginatedPage = (props) => {
  const {
    paginationObj,
    totalProductsCount,
    recordsPerPage,
    paginatedProducts,
    className,
    plpGridPromos,
    plpCrossPromos,
    submitSortChange,
    navTree,
    processedBreadCrumbs,
    isBopisFilterOn,
    isPaginatedPlpLoading,
    isInitialCall,
    isL2Category,
    pageNumber,
    pathName,
    setUpdatedUrlActn,
    isShowL2Skeleton,
    isServiceError,
    isNewPlpPaginatorEnabled,
    isPLPCategoryPillsEnabled,
    isNewPlpPaginatorShowingResultEnabled,
    currentNavIds,
    trackCategoryPillClick,
    isPaginatedAlternatePlpEnabled,
    trackPaginationClick,
    isNewNavigationImagesEnabled,
    sortByRatingLabel,
    isMostFavoritedBadgeEnabled,
    setPLPSLPAddToCartCTA,
    isTileRedesignAB,
    favAddRemoveLoadingState,
    toastModalState,
    showToastModalAction,
    isAlternateImageOnHoverEnabled,
    shouldRenderSkeletonComponent,
    isOneHrFilterEnabled,
    imageConfigWithoutDPR,
    isFacetedNavigationEnabled,
    seoUrlPayload,
    isStageHoldCategoryOosMessage,
    isFullWidthInGridPromoHidden,
    isDynamicColorSwatchesEnabled,
    isHoverToPreviewColorSwatchesEnabled,
    isInteractiveColorSwatchBandEnabled,
    isFacetedNavigationNoIndexApplicableCheck,
    isFourTileGridEnabled,
    isSwatchAsColorUrlEnabled,
  } = props;

  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber);
  const [isPaginatorClicked, setIsPaginatorClicked] = useState(false);
  const [numOfColumns, setNumOfColumns] = useState(null);
  const [numOfProductsInCBC, setNumOfProductsInCBC] = useState(null);
  const [, setDomNode] = useState(null);
  const [pathNameWithoutQuery, urlQueryValues] = pathName.split('?');
  const [, , pageType, cid] = pathNameWithoutQuery.split('/');
  const hasMounted = useHasMounted();
  const { isMobile } = getViewportInfo() || {};

  const defaultColumnCount = isFourTileGridEnabled ? 4 : 5;

  const totalProductGrids = paginatedProducts?.length;
  const gridRef = useRef();
  const newPlpPaginator = isNewPlpPaginatorShowingResultEnabled
    ? newPlpShowingResultPaginator
    : newPaginator;
  const Paginator = isNewPlpPaginatorEnabled ? newPlpPaginator : oldPaginator;
  const gridtemplateColumns = 'grid-template-columns';
  const calculateGridAttributes = () => {
    if (plpCrossPromos?.length && isClient() && gridRef?.current) {
      const gridComputedStyle = window.getComputedStyle(gridRef.current);
      // get number of grid columns
      const gridColumnCount = gridComputedStyle
        .getPropertyValue(gridtemplateColumns)
        .split(' ').length;
      let gridWidth = gridComputedStyle.getPropertyValue('width');
      if (gridWidth) {
        [gridWidth] = gridWidth.split('px');
        gridWidth = gridWidth && parseInt(gridWidth, 10);
        const CBC_LEFT_IMAGE_WIDTH = 466;
        const CBC_PRODUCT_TILE_WIDTH = 391;
        gridWidth -= CBC_LEFT_IMAGE_WIDTH;
        const numberOfTiles = Math.floor(gridWidth / CBC_PRODUCT_TILE_WIDTH);
        setNumOfProductsInCBC(numberOfTiles);
      }
      setNumOfColumns(gridColumnCount);
    }
  };

  useEffect(() => {
    calculateGridAttributes();
  }, [gridRef?.current, plpCrossPromos]);

  useEffect(() => {
    if (pageNumber !== currentPageNumber) setCurrentPageNumber(pageNumber);
    setIsPaginatorClicked(false);
  }, [pageNumber]);

  const pageClick = (page, skipUrlUpdate) => {
    if (page >= 1 && page <= paginationObj?.length && page !== currentPageNumber) {
      const { formDetails, asPath, seoFacetedNavigationConfig, clothingSizeObj } = seoUrlPayload;
      setIsPaginatorClicked(true);
      setCurrentPageNumber(page);
      if (!skipUrlUpdate) {
        const updatedUrl = isFacetedNavigationEnabled
          ? getPlpUrlQueryValuesSeo(
              formDetails,
              asPath,
              true,
              page,
              seoFacetedNavigationConfig,
              clothingSizeObj
            )
          : setUpdatedUrl(pageType, cid, urlQueryValues, page);
        setUpdatedUrlActn({ updatedUrl });
      }
      setTimeout(() => {
        scrollToBreadCrumb();
      }, 10);
    }
  };
  function nearestSlot(slotNum, mod) {
    if (mod > defaultColumnCount) {
      return slotNum + (mod - (slotNum % mod));
    }
    const modVal = slotNum % mod;
    if (modVal > slotNum) {
      return slotNum + modVal;
    }
    return slotNum - modVal;
  }
  const getPromoSlot = (plpPromo, noc) => {
    const [, position, isDouble] = plpPromo?.slot?.split('_');
    const hasDouble = isDouble === 'double';
    const isFullWidth = isDouble === 'fullwidth';
    if (isDouble && !isFullWidth) {
      return { position: position % noc === 0 ? position - 1 : position, hasDouble };
    }
    return !isFullWidth && { position, hasDouble };
  };

  const getGridPromoRow = (plpPromo) => {
    const slotParts = plpPromo?.slot?.split('_');

    if (!slotParts || slotParts.length < 2) {
      return {}; // invalid slot format
    }
    const promoRow = slotParts?.[1];
    if (!promoRow) {
      return {}; // promoRow is null or undefined
    }

    const isFullwidth = slotParts.includes('fullwidth');
    if (!isFullwidth) {
      return {}; // not a fullwidth promo
    }

    const isMobileSlot = slotParts.includes('mb');
    const isDesktopSlot = !isMobileSlot;

    if ((isMobile && isMobileSlot) || (!isMobile && isDesktopSlot)) {
      return { promoRow, isFullWidth: true };
    }
    return {};
  };
  const min = (currentPageNumber - 1) * recordsPerPage;
  const max = currentPageNumber * recordsPerPage;
  const gridExist = gridRef?.current;

  const onRefChange = useCallback((node) => {
    gridRef.current = node;
    setDomNode(node);
  }, []);

  const currentGridNo = useMemo(() => {
    let gridColumnCount = defaultColumnCount;
    if (isClient() && gridExist) {
      const gridComputedStyle = window && window.getComputedStyle(gridRef.current);
      gridColumnCount = gridComputedStyle?.getPropertyValue(gridtemplateColumns).split(' ').length;
    }
    return plpGridPromos
      ? plpGridPromos.reduce((acc, plpPromo) => {
          const { position, hasDouble } = getPromoSlot(plpPromo, gridColumnCount);
          if (position > min && position <= max) {
            acc[position] = { tileSize: hasDouble ? 2 : 1, ...plpPromo };
          }
          return acc;
        }, {})
      : null;
  }, [plpGridPromos, min, gridExist]);

  const gridColumnCount = useMemo(() => {
    if (isClient() && gridExist) {
      const gridComputedStyle = window && window.getComputedStyle(gridRef.current);
      return gridComputedStyle?.getPropertyValue(gridtemplateColumns).split(' ').length;
    }
    return defaultColumnCount; // default value
  }, [gridExist, gridRef, isClient]);

  const currentRowNo = useMemo(() => {
    if (!plpGridPromos) return null;

    return plpGridPromos.reduce((acc, plpPromo) => {
      const { promoRow, isFullWidth } = getGridPromoRow(plpPromo);
      const promoPosition = (parseInt(promoRow, 10) - 1) * gridColumnCount;
      if (promoPosition > min && promoPosition <= max && isFullWidth) {
        acc[promoPosition] = { tileSize: gridColumnCount, ...plpPromo };
      }
      return acc;
    }, {});
  }, [plpGridPromos, gridColumnCount, min, max]);

  const currentCrossGridNo = useMemo(() => {
    return plpCrossPromos?.length && numOfColumns
      ? plpCrossPromos.reduce((acc, plpPromo, idx) => {
          const [, position] = plpPromo?.slot?.split('_');
          let slotNum = parseInt(position, 10);
          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (position > min && position <= max) {
            if (isClient()) {
              slotNum = nearestSlot(slotNum, parseInt(numOfColumns, 10));
              acc[slotNum + 1 + idx] = { ...plpPromo, numberOfProducts: numOfProductsInCBC };
            }
          }
          return acc;
        }, {})
      : null;
  }, [plpCrossPromos, min, numOfColumns]);

  /**
   * The cls issue caused because we fetch limited products on server side
   * @returns number of skeleton to be rendered on server side to solve cls issue
   */
  const getAdditionalProductsCountOnCsr = () => {
    const apiConfig = getAPIConfig();
    const { firstLoadPLPProductCount = 8 } = apiConfig;
    if (isInitialCall) {
      return calculateSkeletonCount(
        totalProductsCount,
        recordsPerPage,
        firstLoadPLPProductCount,
        pathName
      );
    }
    return 0;
  };

  // Determine the class for the PLP grid
  let plpGridClass = 'plp-grid';

  if (isPaginatedAlternatePlpEnabled) {
    plpGridClass += ' alternate-plp';
  } else if (isFourTileGridEnabled) {
    plpGridClass += ' four-grid';
  }
  return (
    <div className={className}>
      <SortByComponent
        submitSortChange={submitSortChange}
        totalProductsCount={totalProductsCount === -1 ? 0 : totalProductsCount}
        sortByRatingLabel={sortByRatingLabel}
        pathName={pathName}
        isOneHrFilterEnabled={isOneHrFilterEnabled}
        isPlp
      />
      {(totalProductsCount === 0 && !isPaginatedPlpLoading && hasMounted) ||
      (isStageHoldCategoryOosMessage && hasMounted) ? (
        <EmptyCategoryPageView
          pathName={pathName}
          navTree={navTree}
          breadCrumbs={processedBreadCrumbs}
          isBopisFilterOn={isBopisFilterOn}
          isServiceError={isServiceError}
          isPlp
        />
      ) : null}
      {(totalProductsCount > 0 || isPaginatedPlpLoading) && !isStageHoldCategoryOosMessage && (
        <>
          <div className={`${plpGridClass}`} ref={onRefChange}>
            {L2CategorySkeleton(
              isPaginatedPlpLoading,
              isL2Category,
              isInitialCall,
              isShowL2Skeleton
            )}
            {isPaginatorClicked &&
              PaginatedPlpSkeleton(isPaginatedPlpLoading, isPaginatorClicked, recordsPerPage)}
            {!shouldRenderSkeletonComponent && totalProductGrids ? (
              <PaginatedView
                {...props}
                additionalProductsCountOnCsr={getAdditionalProductsCountOnCsr()}
                min={min}
                currentGridNo={currentGridNo}
                currentRowNo={currentRowNo}
                gridColumnCount={gridColumnCount}
                currentCrossGridNo={currentCrossGridNo}
                currentPageNumber={currentPageNumber}
                isPaginatorClicked={isPaginatorClicked}
                isMostFavoritedBadgeEnabled={isMostFavoritedBadgeEnabled}
                setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}
                cid={cid}
                isTileRedesignAB={isTileRedesignAB}
                favAddRemoveLoadingState={favAddRemoveLoadingState}
                isAlternateImageOnHoverEnabled={isAlternateImageOnHoverEnabled}
                imageConfigWithoutDPR={imageConfigWithoutDPR}
                isFullWidthInGridPromoHidden={isFullWidthInGridPromoHidden}
                isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
                isHoverToPreviewColorSwatchesEnabled={isHoverToPreviewColorSwatchesEnabled}
                isInteractiveColorSwatchBandEnabled={isInteractiveColorSwatchBandEnabled}
                gridBlockRef={gridRef?.current}
                isSwatchAsColorUrlEnabled={isSwatchAsColorUrlEnabled}
              />
            ) : (
              <StyledPaginatedSkeleton />
            )}
            {!isPaginatorClicked &&
              PaginatedPlpSkeleton(
                isPaginatedPlpLoading,
                false,
                recordsPerPage,
                shouldRenderSkeletonComponent,
                isShowL2Skeleton,
                totalProductGrids
              )}
          </div>
          <Paginator
            className=""
            currentPage={currentPageNumber}
            totalCount={totalProductsCount === -1 ? 0 : totalProductsCount}
            pageSize={recordsPerPage}
            onPageChange={pageClick}
            trackClick={trackPaginationClick}
            processedBreadCrumbs={processedBreadCrumbs}
            isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
            isFacetedNavigationNoIndexApplicableCheck={isFacetedNavigationNoIndexApplicableCheck}
            pathName={pathName}
            cid={cid}
            setUpdatedUrlActn={setUpdatedUrlActn}
          />
        </>
      )}
      {isPLPCategoryPillsEnabled && navTree && Object.keys(navTree)?.length !== 0 && (
        <CategoryPills
          className={className}
          navigation={navTree}
          level={currentNavIds}
          pathName={pathName}
          onClick={trackCategoryPillClick}
        />
      )}
      {toastModalState?.showToast && (
        <ToastModal
          className={className}
          duration={toastModalState?.duration}
          showToastModalAction={showToastModalAction}
        />
      )}
    </div>
  );
};

PaginatedPage.propTypes = {
  pageNumber: PropTypes.number,
  paginationObj: PropTypes.arrayOf(),
  totalProductsCount: PropTypes.number,
  recordsPerPage: PropTypes.number,
  paginatedProducts: PropTypes.arrayOf(),
  onQuickViewOpenClick: PropTypes.func,
  onAddItemToFavorites: PropTypes.func,
  onSetLastDeletedItemIdAction: PropTypes.func,
  wishListData: PropTypes.shape({}),
  plpGridPromos: PropTypes.arrayOf(),
  isBrierleyPromoEnabled: PropTypes.bool,
  isShortImage: PropTypes.bool,
  submitSortChange: PropTypes.func,
  saveProductDataForPdp: PropTypes.func,
  navTree: PropTypes.shape({}),
  isBopisFilterOn: PropTypes.bool,
  processedBreadCrumbs: PropTypes.arrayOf(),
  isPaginatedPlpLoading: PropTypes.bool,
  isInitialCall: PropTypes.bool,
  isL2Category: PropTypes.bool,
  pathName: PropTypes.string,
  setUpdatedUrlActn: PropTypes.func,
  isPlcc: PropTypes.bool,
  isInternationalShipping: PropTypes.bool.isRequired,
  isShowL2Skeleton: PropTypes.bool,
  isTopRatedBackAbEnabled: PropTypes.bool,
  isBestSellerBackAbEnabled: PropTypes.bool,
  isNewPLPAtbCardTileEnabled: PropTypes.bool,
  isAlternatePlpEnabled: PropTypes.bool,
  isPaginatedAlternatePlpEnabled: PropTypes.bool,
  isNewSwatchUrlEnabled: PropTypes.bool,
  currentNavIds: PropTypes.arrayOf(),
  isOneClickATCEnabled: PropTypes.bool,
  addToBagEcom: PropTypes.func,
  isTileRedesignAB: PropTypes.bool,
  isAlternateImageOnHoverEnabled: PropTypes.bool,
  isSwatchAsColorUrlEnabled: PropTypes.bool,
};

PaginatedPage.defaultProps = {
  pageNumber: 1,
  paginationObj: [],
  totalProductsCount: -1,
  recordsPerPage: 30,
  paginatedProducts: [],
  onQuickViewOpenClick: () => {},
  onAddItemToFavorites: () => {},
  onSetLastDeletedItemIdAction: () => {},
  wishListData: {},
  plpGridPromos: [],
  isBrierleyPromoEnabled: false,
  isShortImage: false,
  submitSortChange: () => {},
  saveProductDataForPdp: () => {},
  navTree: {},
  isBopisFilterOn: false,
  processedBreadCrumbs: [],
  isPaginatedPlpLoading: false,
  isInitialCall: false,
  isL2Category: false,
  pathName: '',
  setUpdatedUrlActn: () => {},
  isPlcc: false,
  isShowL2Skeleton: false,
  isTopRatedBackAbEnabled: false,
  // eslint-disable-next-line react/default-props-match-prop-types
  isNewSwatchUrlEnabled: false,
  isBestSellerBackAbEnabled: false,
  isNewPLPAtbCardTileEnabled: false,
  currentNavIds: [],
  isAlternatePlpEnabled: false,
  isPaginatedAlternatePlpEnabled: false,
  isOneClickATCEnabled: false,
  addToBagEcom: () => {},
  isTileRedesignAB: false,
  isAlternateImageOnHoverEnabled: false,
  isSwatchAsColorUrlEnabled: false,
};

export { PaginatedPage as PaginatedPageVanilla };
export default withStyles(PaginatedPage, styles);
